/**
 * The committee account's own profile — /a/profile.
 *
 * A committee member had no way to change their own name, email, mobile,
 * photo or password: the account block in the rail opened /m/profile for
 * members and did nothing at all for admins (owner's report, 27 Aug 2026).
 * That is worse than it sounds — the email is a sign-in identity AND the only
 * password-recovery channel, so a committee account with a stale address is
 * one forgotten password away from being locked out of the club it runs.
 *
 * It is deliberately NOT the member profile with rows hidden. The member
 * screen is built around the bib, the tier and what is owed; a committee
 * account has no bib, pays nothing, and the questions it needs answering are
 * "how do I sign in" and "who is this on the roster". Same atoms, same sheets
 * (ProfileSheet, PasswordSheet), different subject.
 */

import { formatDate, initials, maskEmail } from '../../data';
import { useClubData, useGrounds } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { ProfileSheet } from '../../components/sheets/ProfileSheet';
import { PasswordSheet } from '../../components/sheets/PasswordSheet';
import { Button, Empty, Fact, ViewTitle } from '../../components/ui';

const SEC = 'border-t border-line p-4';
const SEC_HEAD = 'mb-3 flex flex-wrap items-center justify-between gap-2.5';
const SEC_TITLE = 'font-market text-[9.5px] uppercase tracking-[0.13em] text-copy-muted';
const FACT_GRID = 'grid grid-cols-2 gap-x-[18px] gap-y-3.5 min-[560px]:grid-cols-3';
const NOTE = 'mt-3 flex items-start gap-3 border border-line bg-ground px-3.5 py-3';
const NOTE_ICON =
  'grid h-[26px] w-[26px] flex-none place-items-center border border-line bg-white text-copy-muted';
const NOTE_TEXT =
  'text-[12.5px] leading-relaxed text-copy-muted [&_b]:font-semibold [&_b]:text-ink-700';

export function AdminProfilePage() {
  const grounds = useGrounds();
  const { db, me } = useClubData();
  const { openSheet } = useUI();

  if (!me) {
    return (
      <Empty title="No committee record">
        This account is not on the club roster. Sign in again, or ask the portal
        owner to add you to the club.
      </Empty>
    );
  }

  return (
    <>
      <ViewTitle title="My account" sub="Your details on the committee" />

      <div className="mb-3.5 overflow-hidden border border-line bg-white">
        <div className="flex items-center gap-4 p-5">
          <div className="relative grid h-[78px] w-[78px] flex-none place-items-center overflow-hidden rounded-full border border-line bg-line-chip">
            {me.photo ? (
              <img src={me.photo} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="font-market text-[28px] font-extrabold text-copy-label">
                {initials(me.name)}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-market text-[25px] font-extrabold leading-[1.05] tracking-[-0.03em]">
              {me.name}
            </div>
            <div className="mt-1.5 font-market text-[10.5px] leading-relaxed text-copy-muted">
              {db.club?.name ?? 'The club'}
              <br />
              On the committee since {me.joined ? formatDate(me.joined) : '—'}
            </div>
            <div className="mt-2 inline-block bg-gold-50 px-2.5 py-1 font-market text-[11px] font-extrabold uppercase tracking-[0.16em] text-gold-800">
              Committee
            </div>
          </div>
        </div>

        <div className={SEC}>
          <div className={SEC_HEAD}>
            <div className={SEC_TITLE}>Personal details</div>
            {/* A committee account may never run — the emergency block is
                asked for, not enforced. See ProfileSheet. */}
            <Button
              tone="p"
              small
              onClick={() => openSheet(<ProfileSheet member={me} />)}
            >
              Edit profile
            </Button>
          </div>
          <div className={FACT_GRID}>
            <Fact k="Mobile" v={me.phone} mono />
            <Fact k="Email" v={me.email} mono />
            <Fact k="Date of birth" v={me.dob ? formatDate(me.dob) : ''} mono />
            <Fact k="Home ground" v={grounds[me.ground] ?? ''} />
            <Fact k="T-shirt size" v={me.tshirt} />
          </div>
        </div>

        <div className={SEC}>
          <div className={SEC_HEAD}>
            <div className={SEC_TITLE}>Sign-in</div>
            <Button tone="p" small onClick={() => openSheet(<PasswordSheet member={me} />)}>
              {me.hasPassword ? 'Change password' : 'Set a password'}
            </Button>
          </div>
          <div className={FACT_GRID}>
            <Fact k="Sign in with" v="Mobile or email + password" />
            <Fact k="Password" v={me.hasPassword ? 'Set' : 'Not set yet'} />
            <Fact k="Reset link goes to" v={me.email ? maskEmail(me.email) : ''} mono />
          </div>
          <div className={NOTE}>
            <div className={NOTE_ICON}>!</div>
            <div className={NOTE_TEXT}>
              Your email is a <b>sign-in identity</b> and the only address a
              password reset can be sent to. Keep it one you actually read — a
              committee account locked out is a club nobody can administer.
            </div>
          </div>
        </div>

        <div className={SEC}>
          <div className={SEC_HEAD}>
            <div className={SEC_TITLE}>Emergency &amp; medical</div>
          </div>
          {me.emName ? (
            <div className={FACT_GRID}>
              <Fact k="Blood group" v={me.blood} mono />
              <Fact k="Emergency contact" v={me.emName} />
              <Fact k="Relationship" v={me.emRel} />
            </div>
          ) : (
            <div className={NOTE}>
              <div className={NOTE_ICON}>?</div>
              <div className={NOTE_TEXT}>
                No emergency contact on file. <b>Add one</b> from Edit profile if
                you run with the club — every member on the ground has one.
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="text-center font-market text-[10px] text-copy-label">
        {db.members.length} runners on the roster
      </div>
    </>
  );
}
